import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import API from '../../api/api.jsx';

const Prop_002_01 = ({ showModal, handleClose, BranchCodeOptions, StorageStatusOptions, DetailData }) => {

    const { t }                     = useTranslation();

    // 수정여부 체크
    const isEditMode   = !!DetailData;
    const selectedData = DetailData;

    const initName       = isEditMode ? selectedData.name : '';
    const initLocation   = isEditMode ? selectedData.location : '';
    const initStorageId  = isEditMode ? selectedData.storage_id : '';

    // 지점
    const initBranchCode = useMemo(() => (
        showModal ? (isEditMode ? selectedData.branch_code : BranchCodeOptions[0][0]) : ''
    ), [showModal, isEditMode, selectedData, BranchCodeOptions]);
    
    // 사용자상태
    const initStatus = useMemo(() => (
        showModal ? (isEditMode ? selectedData.status : StorageStatusOptions[0][0]) : ''
    ), [showModal, isEditMode, selectedData, StorageStatusOptions]);
        
    const [BranchCode, setBranchCode] = useState(initBranchCode);
    const [Name, setName]             = useState(initName);
    const [Location, setLocation]     = useState(initLocation);
    const [Status, setStatus]         = useState(initStatus);
    const [StorageId, setStorageId]   = useState(initStorageId);
    
    useEffect(() => {
        if (showModal) {
            if (isEditMode) {
                // 수정 모드인 경우 selectedData 값으로 초기화
                if (BranchCode !== selectedData.branch_code) setBranchCode(selectedData.branch_code);
                if (Name !== selectedData.name) setName(selectedData.name);
                if (Location !== selectedData.location) setLocation(selectedData.location);
                if (Status !== selectedData.status) setStatus(selectedData.status);
                if (StorageId !== selectedData.storage_id) setStorageId(selectedData.storage_id);
            } else {
                // isEditMode가 false인 경우 초기 값으로 설정
                setBranchCode(initBranchCode);
                setName(initName);
                setLocation(initLocation);
                setStatus(initStatus);
                setStorageId(initStorageId);
            }
        }
    }, [showModal, isEditMode, initBranchCode, initName, initLocation, initStatus, initStorageId, selectedData]);

    // 지점
    const handleChangeBranch= (e) => {
        setBranchCode(e.target.value);
    };

    // 보관함이름
	const handleChangeName = (e) => {
		setName(e.target.value);
	};

    // 보관함장소
	const handleChangeLocation = (e) => {
		setLocation(e.target.value);
	};

    // 상태
	const handleChangeStatus = (e) => {
		setStatus(e.target.value);
	};

    // 데이터 저장시에 useRef 를 통해서 
    const formRef = useRef(null);

    const handleSave = async (e) => {
        e.preventDefault();

        if (!Name) {
            alert(t(`COLLECTION_MANAGEMENT.이름을 입력하세요`, { defaultValue: '이름을 입력하세요' }));
            return;
        };

        if (!Location) {
            alert(t(`COLLECTION_MANAGEMENT.위치를 입력하세요`, { defaultValue: '위치를 입력하세요' }));
            return;
        };

        try {
            const savedData = await API.fetchProp002DataPost({
                storageId   : StorageId,
                branch_code : BranchCode,
                setName     : Name,
                Location    : Location ,   // parseFloat(Fee.replace(/,/g, '')), // 콤마 제거 후 숫자로 변환
                Status      : Status,
            });

            // 등록
            if (savedData.code === 201) {
                const message = savedData.result.content.message;
                alert(t(`COLLECTION_MANAGEMENT.${message}`, { defaultValue: message }));
                return;
            }

            // 수정
            if (savedData.code === 200) {
                const message = savedData.result.content.message;
                alert(t(`COLLECTION_MANAGEMENT.${message}`, { defaultValue: message }));
                return;
            }

        } catch (error) {
            console.error('보관함 정보 저장 에러:', error);
        } finally {
            handleClose();
        }
        
        try {
            // 수동으로 폼 제출
            formRef.current.submit();
        } catch (error) {
            console.error('수동으로 폼 제출 에러:', error);
            // 폼 제출이 실패했을 때 수행할 로직을 추가하면 됩니다.
        }
    };

    return (
        <Modal show={showModal} onHide={handleClose} centered dialogClassName="modal-lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t('MENU_I18N.보관함 관리', { defaultValue: '보관함 관리' })}</Modal.Title>
            </Modal.Header>

            <Modal.Body className="text-start">
                <div className="row gx-4">
                    <div className="col-lg-12">
                        <div className="card border-1 mb-4">
                            <div className="card-body fw-bold">
                                {/* 폼시작  2023.11.30 추가 전종표 */}
                                {/* ref={formRef}를 사용하여 formRef가 form 엘리먼트를 참조하도록 설정. 
                                그리고 handleSave 함수에서는 이 formRef를 사용하여 submit 메소드를 호출 */}
                                <form ref={formRef} onSubmit={handleSave}>
                                    <div className="form-group mb-0">
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="branch_code_id">{t('COLLECTION_MANAGEMENT.지점', { defaultValue: '지점' })} <span className="text-danger">*</span></label>
                                            <select
                                                className="form-select"
                                                id="branch_code_id"
                                                onChange={handleChangeBranch}
                                                value={BranchCode}
                                            >
                                                {BranchCodeOptions.map((option, index) => (
                                                    <option key={index} value={option[0]}>
                                                        {option[1]}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="storage_name_id">{t('COLLECTION_MANAGEMENT.보관함이름', { defaultValue: '보관함이름' })} <span className="text-danger">*</span></label>
                                            <input className="form-control" type="text" id="storage_name_id" value={Name}  onChange={handleChangeName}  />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="location_id">{t('COLLECTION_MANAGEMENT.보관함 장소', { defaultValue: '보관함 장소' })} <span className="text-danger">*</span></label>
                                            <input className="form-control" type="text" id="location_id" value={Location}  onChange={handleChangeLocation}  />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="storage_status_id">{t('COLLECTION_MANAGEMENT.상태', { defaultValue: '상태' })} <span className="text-danger">*</span></label>
                                            <select
                                                className="form-select"
                                                id="storage_status_id"
                                                onChange={handleChangeStatus}
                                                value={Status}
                                            >
                                                {StorageStatusOptions.map((option, index) => (
                                                    <option key={index} value={option[0]}>
                                                        {t(`${option[1]}.${option[0]}`, { defaultValue: `${option[0]}` })}
                                                        {/* {option[0]} */}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="card-footer">
                                <button className="btn btn-outline-success float-end mt-1 mb-1 me-1 ms-auto w-100px" onClick={handleSave}>{t('COLLECTION_MANAGEMENT.저장', { defaultValue: '저장' })} <i className="fa fa-sign-in"></i></button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="d-block">
                <button className="btn btn-outline-gray me-1 mb-1 w-auto" onClick={handleClose}>
                    <i className="fa fa-backward"></i> {t('COLLECTION_MANAGEMENT.취소', { defaultValue: '취소' })}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default Prop_002_01;